import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import {
  CertificationRecordSettings,
  CertificationRecordsDraftHeader,
  CertificationRecordsFilters,
  CertificationRecordsUiState,
  EligibleCertificationForRecord,
} from "./certification-records.interface";
import { certificationRecordsApi } from "./certification-recordsApi";

const defaultListFilters: CertificationRecordsFilters = {
  search: "",
  year: "",
  page: 1,
  size: 20,
};

const defaultSettings: CertificationRecordSettings = {
  estados: [],
  tipos_periodo: [],
};

const defaultDraftHeader: CertificationRecordsDraftHeader = {
  period_type_id: null,
  period_start: null,
  period_end: null,
  issue_date: null,
  title: "",
  notes: "",
};

const resolveDefaultPeriodTypeId = (
  currentPeriodTypeId: number | null,
  settings: CertificationRecordSettings
): number | null => {
  if (
    currentPeriodTypeId &&
    settings.tipos_periodo.some((item) => item.id === currentPeriodTypeId)
  ) {
    return currentPeriodTypeId;
  }

  return settings.tipos_periodo[0]?.id ?? null;
};

const initialState: CertificationRecordsUiState = {
  listFilters: defaultListFilters,
  generateFilters: {
    search: "",
    page: 1,
    size: 20,
  },
  collectionsFilters: {
    search: "",
    page: 1,
    size: 20,
  },
  settings: defaultSettings,
  draftHeader: defaultDraftHeader,
  draftItems: [],
  selectedRecordId: null,
  selectedCollectRecordId: null,
};

const certificationRecordsSlice = createSlice({
  name: "certificationRecords",
  initialState,
  reducers: {
    setListFilters: (state, action: PayloadAction<Partial<CertificationRecordsFilters>>) => {
      state.listFilters = { ...state.listFilters, ...action.payload };
    },
    setGenerateFilters: (state, action: PayloadAction<Partial<Omit<CertificationRecordsFilters, "year">>>) => {
      state.generateFilters = { ...state.generateFilters, ...action.payload };
    },
    setCollectionsFilters: (state, action: PayloadAction<Partial<Omit<CertificationRecordsFilters, "year">>>) => {
      state.collectionsFilters = { ...state.collectionsFilters, ...action.payload };
    },
    setDraftHeader: (state, action: PayloadAction<Partial<CertificationRecordsDraftHeader>>) => {
      state.draftHeader = { ...state.draftHeader, ...action.payload };
    },
    addDraftItem: (state, action: PayloadAction<EligibleCertificationForRecord>) => {
      const exists = state.draftItems.some((item) => item.id === action.payload.id);
      if (!exists) {
        state.draftItems.push(action.payload);
      }
    },
    removeDraftItem: (state, action: PayloadAction<number>) => {
      state.draftItems = state.draftItems.filter((item) => item.id !== action.payload);
    },
    clearDraft: (state) => {
      state.draftHeader = {
        ...defaultDraftHeader,
        period_type_id: resolveDefaultPeriodTypeId(null, state.settings),
      };
      state.draftItems = [];
    },
    setSelectedRecordId: (state, action: PayloadAction<number | null>) => {
      state.selectedRecordId = action.payload;
    },
    setSelectedCollectRecordId: (state, action: PayloadAction<number | null>) => {
      state.selectedCollectRecordId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      certificationRecordsApi.endpoints.getCertificationRecordSettings.matchFulfilled,
      (state, action) => {
        if (!action.payload.success || !action.payload.data) {
          return;
        }

        state.settings = action.payload.data;
        state.draftHeader.period_type_id = resolveDefaultPeriodTypeId(
          state.draftHeader.period_type_id,
          action.payload.data
        );
      }
    );
  },
});

export const selectCertificationRecordsListFilters = (state: RootState) => state.certificationRecords.listFilters;
export const selectCertificationRecordsGenerateFilters = (state: RootState) => state.certificationRecords.generateFilters;
export const selectCertificationRecordsCollectionsFilters = (state: RootState) => state.certificationRecords.collectionsFilters;
export const selectCertificationRecordSettings = (state: RootState) => state.certificationRecords.settings;
export const selectCertificationRecordStates = (state: RootState) => state.certificationRecords.settings.estados;
export const selectCertificationRecordPeriodTypes = (state: RootState) => state.certificationRecords.settings.tipos_periodo;
export const selectCertificationRecordsDraftHeader = (state: RootState) => state.certificationRecords.draftHeader;
export const selectCertificationRecordsDraftItems = (state: RootState) => state.certificationRecords.draftItems;
export const selectCertificationRecordsSelectedId = (state: RootState) => state.certificationRecords.selectedRecordId;
export const selectCertificationRecordsSelectedCollectId = (state: RootState) => state.certificationRecords.selectedCollectRecordId;

export const {
  setListFilters,
  setGenerateFilters,
  setCollectionsFilters,
  setDraftHeader,
  addDraftItem,
  removeDraftItem,
  clearDraft,
  setSelectedRecordId,
  setSelectedCollectRecordId,
} = certificationRecordsSlice.actions;

export default certificationRecordsSlice.reducer;
